// Redux
import { AsyncResult, Action, ApiAction } from '../../index';
import api from '../../redux-api';
import { batch } from 'react-redux';

// Types
import Alt from '@/types/models/alt';
import PERIOD_END_DATA_OPTIONS from '@/types/period-end-data-options';
import { AltsStoreState } from '../types/store-alts';
import { ReduxState } from '../types';

// Utils
import { getMonthEndDataOption } from '@/utils/comparison';
import { updateAltWithDisclaimers } from '@/utils/disclaimers/alts';

// Initial State
export const INITIAL_STATE = {
  alts: {},
  altsPMED: {},
  altsRMED: {},
  altsPQED: {},
  altsRQED: {},
};

export const ALTS_STORE_BASE_PATH = 'connect/alts/store';

// Actions
const FETCH_ALTS_BY_ID: ApiAction = {
  type: `${ALTS_STORE_BASE_PATH}/fetch`,
  endpoint: 'alts/:id/',
};

const FETCH_MANY_ALTS: ApiAction = {
  type: `${ALTS_STORE_BASE_PATH}/fetch-many`,
  endpoint: 'alts/',
};

const ALTS_STORE_GET = `${ALTS_STORE_BASE_PATH}/get`;
const ALTS_STORE_GET_MANY = `${ALTS_STORE_BASE_PATH}/get-many`;
const ALTS_STORE_ADD = `${ALTS_STORE_BASE_PATH}/add`;

// Reducer
export default function reducer(state: AltsStoreState = INITIAL_STATE, action: Action): AltsStoreState {
  const { alt } = action;
  switch (action.type) {
    case ALTS_STORE_ADD:
      switch (action.monthEndDataOption) {
        case PERIOD_END_DATA_OPTIONS.RECENT_MONTH_END:
          return {
            ...state,
            altsRMED: { ...state.altsRMED, [alt._id]: alt },
          };
        case PERIOD_END_DATA_OPTIONS.PREVIOUS_MONTH_END:
          return {
            ...state,
            altsPMED: { ...state.altsPMED, [alt._id]: alt },
          };
        case PERIOD_END_DATA_OPTIONS.RECENT_QUARTER_END:
          return {
            ...state,
            altsRQED: { ...state.altsRQED, [alt._id]: alt },
          };
        case PERIOD_END_DATA_OPTIONS.PREVIOUS_QUARTER_END:
          return {
            ...state,
            altsPQED: { ...state.altsPQED, [alt._id]: alt },
          };
        default:
          return {
            ...state,
            alts: { ...state.alts, [alt._id]: alt },
          };
      }
    default:
      return state;
  }
}

const getAltFromAltsState = (state: ReduxState, id: string, monthEndDataOption?: PERIOD_END_DATA_OPTIONS) => {
  // See if we got it in State
  let altState;
  const altsStore = state.alts.store;

  switch (monthEndDataOption) {
    case PERIOD_END_DATA_OPTIONS.RECENT_MONTH_END:
      altState = altsStore.altsRMED[id];
      break;
    case PERIOD_END_DATA_OPTIONS.PREVIOUS_MONTH_END:
      altState = altsStore.altsPMED[id];
      break;
    case PERIOD_END_DATA_OPTIONS.RECENT_QUARTER_END:
      altState = altsStore.altsRQED[id];
      break;
    case PERIOD_END_DATA_OPTIONS.PREVIOUS_QUARTER_END:
      altState = altsStore.altsPQED[id];
      break;
    default:
      altState = altsStore.alts[id];
      break;
  }

  return altState;
};

// Action Creators

/**
 * Retrieve Alt data either from memory, local cache or directly from the API
 * @param id Alt's unique identifier
 */
export const getAltData =
  (id: string, errorWhiteList?: string[]): AsyncResult<Alt> =>
  (dispatch, getState) => {
    const state = getState();
    const {
      comparison,
      intl: { locale },
    } = state;

    dispatch({ type: ALTS_STORE_GET, id });

    const monthEndDataOption = getMonthEndDataOption(comparison);

    const altState = getAltFromAltsState(state, id, monthEndDataOption);
    if (altState) {
      return Promise.resolve(altState);
    }

    return api.get(FETCH_ALTS_BY_ID, { id, ...(monthEndDataOption && { monthEndDataOption }) }, undefined, errorWhiteList).then(({ alt }: { alt: Alt }) => {
      alt = updateAltWithDisclaimers(alt, locale);
      dispatch({ type: ALTS_STORE_ADD, alt, monthEndDataOption });
      return alt;
    });
  };

/**
 * Retrieve multiple Alts data either from memory, local cache or directly from the API
 * @param ids Alts' unique identifiers
 */
export const getManyAltsData =
  (rawIds: string[]): AsyncResult<Alt[]> =>
  (dispatch, getState) => {
    const state = getState();
    const {
      comparison,
      intl: { locale },
    } = state;

    const ids: string[] = rawIds.filter(Boolean);
    dispatch({ type: ALTS_STORE_GET_MANY, ids });

    const monthEndDataOption = getMonthEndDataOption(comparison);

    const remaining = new Set();
    const altsData = [] as Alt[];

    for (const id of ids) {
      const altState = getAltFromAltsState(state, id, monthEndDataOption);
      if (altState) {
        altsData.push(altState);
      } else {
        remaining.add(id);
      }
    }
    // Fetch the rest from the API if we're missing any
    if (remaining.size) {
      return api
        .get(FETCH_MANY_ALTS, { ids: Array.from(remaining.values()).join('-'), ...(monthEndDataOption && { monthEndDataOption }) })
        .then(({ alts }: { alts: Alt[] }) => {
          batch(() => {
            for (let alt of alts) {
              alt = updateAltWithDisclaimers(alt, locale);
              dispatch({ type: ALTS_STORE_ADD, alt, monthEndDataOption });
            }
          });

          return [...altsData, ...alts];
        });
    }

    // Return the cached alts if no API call is required
    return Promise.resolve(altsData);
  };
